import { CurrencyCode, FinalAmountAssessment } from "@/types";

// Turns one assessment of the backend's final amount warning into the lines the dialog shows. Nothing is decided
// here: the backend chose the reference, the maximum and the rules, and this only picks what to word and in which
// order.

export type AssessmentReason =
  | { kind: "wrongUnit"; direction: "high" | "low"; factor: number }
  | { kind: "deviation"; direction: "high" | "low" }
  | { kind: "maximum" };

export interface AssessmentLines {
  expected: { amountKg: number; source: "ORDER_ESTIMATE" | "CONTRACT_HISTORY"; historySampleSize?: number } | null;
  maximum: { amountKg: number; source: "FRACTION" | "CONTRACT" } | null;
  settlement: { amount: number; currency: CurrencyCode } | null;
  reasons: AssessmentReason[];
}

export const getAssessmentLines = (assessment: FinalAmountAssessment): AssessmentLines => {
  const { reference, rules, amountKg } = assessment;

  const expected: AssessmentLines["expected"] =
    reference?.source === "ORDER_ESTIMATE" || reference?.source === "CONTRACT_HISTORY"
      ? {
          amountKg: reference.amountKg,
          source: reference.source,
          ...(reference.historySampleSize != null && { historySampleSize: reference.historySampleSize }),
        }
      : null;

  const maximum: AssessmentLines["maximum"] =
    assessment.maximum ??
    (reference?.source === "FRACTION_MAXIMUM"
      ? { amountKg: reference.amountKg, source: "FRACTION" }
      : reference?.source === "CONTRACT_MAXIMUM"
        ? { amountKg: reference.amountKg, source: "CONTRACT" }
        : null);

  const reasons: AssessmentReason[] = [];
  if (expected) {
    const direction = amountKg > expected.amountKg ? "high" : "low";
    if (rules.includes("R3_SUSPECTED_UNIT")) {
      const factor = Math.round(Math.max(amountKg, expected.amountKg) / Math.min(amountKg, expected.amountKg));
      reasons.push({ kind: "wrongUnit", direction, factor });
    } else if (rules.includes("R1_DEVIATION")) {
      reasons.push({ kind: "deviation", direction });
    }
  }
  if (rules.includes("R2_MAXIMUM")) reasons.push({ kind: "maximum" });

  return { expected, maximum, settlement: assessment.settlementEstimate ?? null, reasons };
};

/** Identifies an assessment within its warning: `${orderId}:${additionalStreamIndex ?? "original"}`. */
export const assessmentKey = (assessment: Pick<FinalAmountAssessment, "orderId" | "additionalStreamIndex">) =>
  `${assessment.orderId}:${assessment.additionalStreamIndex ?? "original"}`;
